import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'react-bootstrap';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons';

import { get } from 'helpers/api';
import SignedCA from './signed_ca';
import VaultActivityTable from './vault_activity_table';
import InviteToViewDocsLink from './invite_to_view_docs_link';
import { approveLeadVaultAccess, revokeLeadVaultAccess } from 'bundles/project/actions/index';
import { CAN_APPROVE_LEAD_VAULT_ACCESS_TYPES, CAN_REVOKE_LEAD_VAULT_ACCESS_TYPES } from '../../../../reducers/leads';
import NotFullyFinishedSection from './not_fully_finished_section';
import { refreshLead, updateLeadFields, updateLeadLocally } from '../../../../actions/leads';
import NotFinishedProfileIcon from './not_finished_profile_icon';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import ShareLinkByEmailModal from 'components/documents/share_link_by_email_modal';
import { getProject } from '../../../../selectors';

export function VaultActivity({
  approveLeadVaultAccess,
  documentCenter,
  propertyVaultProfile,
  lead,
  refreshLead,
  revokeLeadVaultAccess,
  updateLeadLocally,
  updateLeadFields,
  project,
  useVaultGates
}) {
  const [activity, setActivity] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCaInviteModal, setShowCaInviteModal] = useState(false);
  const openCaInviteModal = () => setShowCaInviteModal(true);
  const closeCaInviteModal = () => {
    setShowCaInviteModal(false);
    refreshLead(lead);
  };
  const SHARE_DOC_MODAL_DESCRIPTION =
    `Now that you have uploaded a CA for ${lead.leadData.name}, send them an invite to view your locked documents.`;
  const WITH_GATES = ` Once sent, the invitee will adhere to the remaining CA & Access settings you have configured
    for your documents.`;
  const WITHOUT_GATES = ' The invitee will skip the waitlist.';

  const {
    activityPath,
    approvalState,
    approveAccessPath,
    id,
    inWaitlist,
    revokeAccessPath,
    saleOrLease,
    verified,
    waitlistStatusLabel,
    followUpPath
  } = propertyVaultProfile;
  React.useEffect(() => {
    if (activityPath) {
      get(activityPath).then((data) => {
        setActivity(data);
        setLoading(false);
      });
    }
  }, [lead.emailAddress, lead.propertyVaultProfiles]);

  const updateActivity = (data) => {
    setActivity({ ...activity, caDownloadPath: data.caUrl, caName: data.caLinkLabel, isValidCa: true });
    refreshLead(lead); // so CA banner can update
    if (lead.leadData.email) openCaInviteModal();
  };

  const isFullyFinished = !!lead.profileUserType;

  // TODO: showCaSection & caContent are dependent on common assumptions with the backend; can we make the communication
  // more explicit to avoid fragility?
  const showCaSection = () => {
    if (documentCenter) return true;

    // On Doc Sharing (legacy), if there's no upload path, that means no profile exists (and therefore no PVPs)
    // NOTE: an upload path exists even if a CA is already uploaded (can we make this more intuitive?)
    if (activity.caUploadPath) return true;
  };

  const caSection = () => {
    if (!showCaSection()) return;

    return (
      <div className="mt-5">
        <h4 className="mt-0">
          <strong>Confidentiality Agreement</strong>
        </h4>
        {caContent()}
      </div>
    );
  };

  const caContent = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center mt-4">
          <FontAwesomeIcon icon={faSpinner} size="2x" spin/>
        </div>
      );
    }

    if (activity.caUploadPath) {
      return (
        <SignedCA
          caDownloadPath={activity.caDownloadPath}
          caName={activity.caName}
          caUploadPath={activity.caUploadPath}
          isDisabled={grayedOut}
          isValidCa={activity.isValidCa}
          lead={lead}
          onSuccess={updateActivity}
          refreshLead={refreshLead}
          saleOrLease={saleOrLease}
          signedCa={activity.caSignedAt !== null}
        />
      );
    } else { // This only happens on Doc Center due to showCaSection logic
      return (
        <p className="mb-0">To upload a CA, set an email on this lead's contact</p>
      );
    }
  };

  let accountStatus;
  if (lead.profileId && !isFullyFinished) {
    accountStatus = 'Not complete';
  } else if (inWaitlist) {
    accountStatus = waitlistStatusLabel;
  } else if (verified) {
    accountStatus = 'Verified';
  } else {
    accountStatus = 'Not Verified';
  }

  let actionNode, actionFunction, actionText;
  if (inWaitlist && CAN_APPROVE_LEAD_VAULT_ACCESS_TYPES.includes(approvalState)) {
    actionFunction = () => approveLeadVaultAccess(lead.id, id, approveAccessPath);
    actionText = 'Approve Access';
  } else if (inWaitlist && CAN_REVOKE_LEAD_VAULT_ACCESS_TYPES.includes(approvalState)) {
    actionFunction = () => revokeLeadVaultAccess(lead.id, id, revokeAccessPath);
    actionText = 'Revoke Access';
  }

  if (actionFunction) {
    const onClick = (e) => {
      e.preventDefault();
      actionFunction();
    };

    actionNode = (
      <React.Fragment>
        <span className="mx-2">|</span>
        <a className="clickable" href="#" name="waitlist-action" onClick={onClick}>
          {actionText}
        </a>
      </React.Fragment>
    );
  }

  const grayedOut = () => {
    if (saleOrLease === 'Sale') {
      return !project.saleChecked;
    } else if (saleOrLease === 'Lease') {
      return !project.leaseChecked;
    }
  };

  return (
      <div style={{ opacity: grayedOut() ? 0.5 : 1 }}>
        <hr></hr>
        <div className="mb-3">
          <span className="h3">{saleOrLease} Status</span>
        </div>
        <div>
          {caSection()}
          {!isEmpty(activity.docActivity) && isFullyFinished && (
            <div className="mt-5">
              <h4 className="mt-0">
                <strong>Site Activity</strong>
              </h4>
              <VaultActivityTable activities={activity.docActivity} />
            </div>
          )}
          <ShareLinkByEmailModal
            additionalInviteProps={{ sale_or_lease: propertyVaultProfile.saleOrLease.toLowerCase() }}
            description={`${SHARE_DOC_MODAL_DESCRIPTION}${useVaultGates ? WITH_GATES : WITHOUT_GATES}`}
            email={lead.leadData.email}
            fromEmails={project.brokersForShareDocs}
            hideModal={closeCaInviteModal}
            leadId={lead.id}
            propertyId={project.id}
            show={showCaInviteModal}
            title="Send email to view documents"
          />
        </div>
      </div>
    );
}

VaultActivity.propTypes = {
  approveLeadVaultAccess: PropTypes.func.isRequired,
  documentCenter: PropTypes.bool.isRequired,
  lead: PropTypes.shape({
    id: PropTypes.number.isRequired,
    profileUserType: PropTypes.string
  }).isRequired,
  project: PropTypes.object.isRequired,
  propertyVaultProfile: PropTypes.shape({
    activityPath: PropTypes.string,
    approvalState: PropTypes.string,
    approveAccessPath: PropTypes.string,
    id: PropTypes.number,
    inWaitlist: PropTypes.bool,
    revokeAccessPath: PropTypes.string,
    saleOrLease: PropTypes.string.isRequired
  }).isRequired,
  refreshLead: PropTypes.func.isRequired,
  revokeLeadVaultAccess: PropTypes.func.isRequired,
  updateLeadFields: PropTypes.func.isRequired,
  updateLeadLocally: PropTypes.func.isRequired,
  useVaultGates: PropTypes.bool.isRequired
};

const actions = {
  approveLeadVaultAccess,
  refreshLead,
  revokeLeadVaultAccess,
  updateLeadLocally,
  updateLeadFields
};

const mapStateToProps = (state) => {
  return {
    project: getProject(state),
    useVaultGates: state.project.useVaultGates
  };
};

export default connect(mapStateToProps, actions)(VaultActivity);
